import { syncUrlComplete, updateUiParam, resetUiParams } from './redux/actions';
import { parseUrlToState, buildUrlString } from './utils';
import { urlSchema } from 'js/url/schema';
import store from 'js/store';

// --- INTERNAL HELPER ---
/**
 * Internal helper to get the current URL state slice from Redux.
 * @returns {Object} The current url state object.
 */
const getUrlState = () => store.getState().url || {};

/**
 * Helper to get a value from an object by a string path (e.g., 'parameters.dateIn').
 * @param {Object} obj
 * @param {string} path
 * @returns {*}
 */
const getValueByPath = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

/**
 * INTERNAL HELPER: Centralizes the logic for finalizing URL changes.
 * 1. Serializes the new parameters into a query string.
 * 2. Pushes the new URL to Browser History (if changed).
 * 3. Dispatches the `syncUrlComplete` action to update the Redux store.
 * @param {Object} nextParams - The final object of parameters to commit.
 * @param {Boolean} updateUrl - If true, updates the browser's address bar history.
 */
const commitToUrl = (nextParams, updateUrl = true) => {
  const newSearchString = buildUrlString(nextParams);
  const newLocationSearch = newSearchString ? `?${newSearchString}` : '';

  const newUrl = `${window.location.pathname}${newLocationSearch}`;

  if (window.location.search !== newLocationSearch) {
    if (updateUrl) {
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }

    store.dispatch(syncUrlComplete({
      queryString: newLocationSearch,
      parameters: nextParams
    }));
  }
};

/**
 * Universal URL management module.
 * Can be used inside React components or externally (e.g., Drupal Behaviors).
 */
export const useUrl = () => {
  const url = {
    get parameters() { return getUrlState().parameters; },
    get uiParams() { return getUrlState().uiParams; },
    get queryString() { return getUrlState().queryString; },

    /**
     * Returns the current URL state slice.
     * @returns {Object} The url state slice.
     */
    getState: () => getUrlState(),

    /**
     * INIT: Parse Browser URL -> Store
     */
    initUrl: () => {
      const currentSearch = window.location.search;
      const parsedParams = parseUrlToState(currentSearch);

      store.dispatch(syncUrlComplete({
        queryString: currentSearch,
        parameters: parsedParams
      }));
    },

    /**
     * EDIT: Update values in the Redux Draft (uiParams)
     * Does NOT change URL yet.
     * @param {Object} newData - Key-value pairs to update in uiParams.
     */
    setParam: (newData) => {
      store.dispatch(updateUiParam(newData));
    },

    /**
     * IMMEDIATE UPDATE: Updates parameters AND pushes changes to URL immediately.
     * Uses OFFICIAL 'parameters' as base, merges newData, and commits.
     * Useful for Pagination, Sorting (ignores unsaved Draft changes).
     * @param {Object} newData - Key-value pairs to merge and commit.
     * @param {Boolean} updateUrl - Whether to update the browser address bar.
     */
    setParamImmediate: (newData, updateUrl = true) => {
      const currentParams = getUrlState().parameters || {};
      store.dispatch(updateUiParam(newData));

      const nextParams = {
        ...currentParams,
        ...newData,
      };

      commitToUrl(nextParams, updateUrl);
    },

    /**
     * COMMIT: uiParams -> Browser URL -> parameters
     * Uses DRAFT 'uiParams' as base and commits it to URL.
     * @param {Boolean} updateUrl - Whether to update the browser address bar.
     */
    applyChanges: (updateUrl = true) => {
      const currentUiParams = getUrlState().uiParams || {};
      commitToUrl(currentUiParams, updateUrl);
    },

    /**
     * PREVIEW: Generates a query string based on the current Draft state.
     * @returns {string} The predicted query string (e.g., "?q=Paris").
     */
    getPredictedQueryString: () => {
      const currentUiParams = getUrlState().uiParams;
      if (!currentUiParams) return '';
      const newSearchString = buildUrlString(currentUiParams);
      return newSearchString ? `?${newSearchString}` : '';
    },

    /**
     * Resets specific parameters to their default values in the UI draft state.
     * @param {string|string[]} params - A single parameter key or an array of keys to reset.
     */
    resetParam: (params) => {
      const paramsToReset = typeof params === 'string' ? [params] : params;
      const resetedData = paramsToReset.reduce((reducer, param) => {
        const paramInfo = urlSchema[param];
        if (paramInfo) {
          reducer[param] = paramInfo.defaultValue;
        }
        return reducer;
      }, {});
      url.setParam(resetedData);
    },

    /**
     * Resets specific parameters to default and immediately updates the URL.
     * @param {string|string[]} params - A single parameter key or an array of keys to reset.
     */
    resetParamImmediate: (params) => {
      const paramsToReset = typeof params === 'string' ? [params] : params;
      const resetedData = paramsToReset.reduce((reducer, param) => {
        const paramInfo = urlSchema[param];
        if (paramInfo) {
          reducer[param] = paramInfo.defaultValue;
        }
        return reducer;
      }, {});
      url.setParamImmediate(resetedData);
    },

    /**
     * ROLLBACK: parameters -> uiParams
     * Discards any unapplied changes in the UI draft state.
     */
    discardChanges: () => {
      store.dispatch(resetUiParams());
    },

    /**
     * Subscribes to changes in specific parts of the URL state.
     * @param {string|string[]} keys - A single path or an array of paths to watch (e.g., 'parameters.dateIn').
     * @param {Function} callback - Called with (newValue, oldValue, urlStore).
     * If keys is an array, newValue/oldValue will be objects with those keys.
     * @returns {Function} Unsubscribe function.
     */
    subscribe: (callback, keys) => {
      const isArray = Array.isArray(keys);
      const keyList = isArray ? keys : [keys];

      // Helper to capture current values for all watched keys
      const captureValues = (state) => {
        return keyList.reduce((acc, key) => {
          acc[key] = getValueByPath(state, key);
          return acc;
        }, {});
      };

      let lastValues = captureValues(getUrlState());

      return store.subscribe(() => {
        const currentState = getUrlState();
        const currentValues = captureValues(currentState);

        // Check if at least one value has changed
        const changedKey = keyList.find(key => currentValues[key] !== lastValues[key]);

        if (changedKey) {
          const oldValuesSnapshot = lastValues;
          lastValues = currentValues;

          // If a single key was requested, pass raw values. If array, pass objects.
          if (isArray) {
            callback(currentValues, oldValuesSnapshot, currentState);
          } else {
            callback(currentValues[keys], oldValuesSnapshot[keys], currentState);
          }
        }
      });
    }
  };

  return url;
};
